import { Axis } from '../axis';
import { DrawPass } from '../chart';
import { RasterLayerSpecification } from '../chart-props';
import { RasterSource } from '../source/raster-source';

type LayerState = {
    canvas: OffscreenCanvas;
};

export function drawRasterLayer(
    pass: DrawPass,
    source: RasterSource,
    layer: RasterLayerSpecification,
    xAxis: Axis,
    yAxis: Axis,
    state?: LayerState
): LayerState | undefined {
    if (!state || source.isDirty()) {
        const canvas = state?.canvas ?? new OffscreenCanvas(256, 256);
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, 256, 256);
        const imgData = ctx.createImageData(256, 256);

        const bounds = source.getBounds();
        const xStep = (bounds[2] - bounds[0]) / 256;
        const yStep = (bounds[3] - bounds[1]) / 256;

        for (let y = 0; y < 256; y++) {
            for (let x = 0; x < 256; x++) {
                const i = y * 256 * 4 + x * 4;
                const v = source.getValue(bounds[0] + x * xStep, bounds[1] + y * yStep);
                if (!v) {
                    continue;
                }
                const t = layer.getValue(v);
                const idx = layer.colorRamp.findIndex((x) => t <= x.value)!;
                let color: (typeof layer.colorRamp)[0]['color'];
                if (idx === 0) {
                    color = layer.colorRamp[idx].color;
                } else {
                    const c1 = layer.colorRamp[idx - 1];
                    const c2 = layer.colorRamp[idx];
                    const a = (t - c1.value) / (c2.value - c1.value);
                    color = lerp(c1.color, c2.color, a);
                }

                imgData.data[i + 0] = color[0];
                imgData.data[i + 1] = color[1];
                imgData.data[i + 2] = color[2];
                imgData.data[i + 3] = color[3];
            }
        }
        ctx.putImageData(imgData, 0, 0);

        state = { canvas };
    }

    const [l, t, r, b] = source.getBounds();

    pass.drawer.ctx.globalAlpha = layer.paint?.opacity ?? 1.0;
    pass.drawer.ctx.drawImage(
        state.canvas,
        0,
        0,
        256,
        256,
        xAxis.project(l, pass.viewState),
        yAxis.project(t, pass.viewState),
        xAxis.project(r, pass.viewState) - xAxis.project(l, pass.viewState),
        yAxis.project(b, pass.viewState) - yAxis.project(t, pass.viewState)
    );

    pass.drawer.ctx.globalAlpha = 1.0;

    return state;
}

function lerp(a: [number, number, number, number], b: [number, number, number, number], t: number): [number, number, number, number] {
    // prettier-ignore
    return [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
        a[3] + (b[3] - a[3]) * t,
    ];
}
